import { ChevronDownIcon, TelescopeIcon, WrenchIcon } from 'lucide-react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAgentOptions } from '@/features/agent/AgentOptionsContext'
import { cn } from '@/utils/cn'

const pillClass = (active: boolean) =>
  cn(
    'inline-flex h-9 items-center gap-1.5 rounded-full border px-3 font-medium text-xs transition-colors md:h-7',
    'disabled:cursor-not-allowed disabled:opacity-50',
    active
      ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15'
      : 'border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground'
  )

/** "Deep research" pill — toggles the per-thread deep mode. The lit (primary)
 * state doubles as the active indicator, so there is no separate badge. */
const DeepResearchToggle: FC = () => {
  const { t } = useTranslation()
  const { options, updateOptions } = useAgentOptions()
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-pressed={options.deep_mode}
          aria-label={t('chat.toolsPanel.deepResearch')}
          className={pillClass(options.deep_mode)}
          onClick={() => updateOptions({ ...options, deep_mode: !options.deep_mode })}
        >
          <TelescopeIcon className="size-3.5" />
          {/* Icon-only in very narrow side-chats (root is a @container). */}
          <span className="hidden @[26rem]:inline">{t('chat.toolsPanel.deepResearch')}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{t('chat.toolsPanel.deepModeHint')}</TooltipContent>
    </Tooltip>
  )
}

/** "Tools" pill — opens the per-tool enable/disable list. Shows the number of
 * disabled tools (and lights up) when the thread is constrained. */
const ToolsPill: FC = () => {
  const { t } = useTranslation()
  const { options, availableTools, updateOptions } = useAgentOptions()
  const disabled = new Set(options.disabled_tools)

  const toggleTool = (name: string, enabled: boolean) => {
    const next = enabled
      ? options.disabled_tools.filter(n => n !== name)
      : [...options.disabled_tools, name]
    updateOptions({ ...options, disabled_tools: next })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('chat.toolsPanel.tools')}
          className={pillClass(disabled.size > 0)}
        >
          <WrenchIcon className="size-3.5" />
          <span className="hidden @[26rem]:inline">{t('chat.toolsPanel.tools')}</span>
          <ChevronDownIcon className="size-3" />
        </button>
      </PopoverTrigger>
      {/* Stronger elevation than the popover default — the panel opens over
          chat text and the theme's popover/background tokens sit too close
          for shadow-md alone (it visually melts into the thread). */}
      <PopoverContent
        align="start"
        side="top"
        className="w-80 max-w-[calc(100vw-2rem)] border border-border bg-popover p-0 text-sm shadow-xl ring-foreground/15"
      >
        <div className="px-3 pt-2.5 pb-1">
          <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {t('chat.toolsPanel.tools')}
          </p>
        </div>
        <div className="max-h-72 overflow-y-auto pb-2">
          {availableTools.map(tool => {
            const enabled = !disabled.has(tool.name)
            const description = t(`chat.toolDescriptions.${tool.name}`, {
              defaultValue: tool.description,
            })
            return (
              <div
                key={tool.name}
                className="flex items-center justify-between gap-3 px-3 py-1.5 max-md:relative"
              >
                {/* Translated label/description with the backend (model-facing,
                    English) text as fallback for unmapped tools. Rows truncate,
                    so hovering shows the full description below the row. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="min-w-0">
                      <p
                        className={cn(
                          'truncate font-medium text-xs',
                          !enabled && 'line-through opacity-60'
                        )}
                      >
                        {t(`chat.toolNames.${tool.name}`, { defaultValue: tool.name })}
                      </p>
                      <p className="text-muted-foreground text-xs max-md:line-clamp-3 md:truncate">
                        {description}
                      </p>
                    </div>
                  </TooltipTrigger>
                  {/* z above the popover's z-[300] — the tooltip portals to
                      the body and would otherwise slide behind the panel. */}
                  <TooltipContent side="bottom" align="start" className="z-[310] max-w-64">
                    {description}
                  </TooltipContent>
                </Tooltip>
                {/* Phone: the switch's hit area (its ::after) grows to the whole
                    row, so the 32×18 control is tappable anywhere on the line. */}
                <Switch
                  className="max-md:static max-md:after:inset-0"
                  checked={enabled}
                  onCheckedChange={checked => toggleTool(tool.name, checked)}
                  aria-label={tool.name}
                />
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** The composer's control pills (deep research + tools), rendered below the
 * input as the first items of the hints row. */
export const AgentComposerControls: FC = () => {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <ToolsPill />
      <DeepResearchToggle />
    </div>
  )
}
